/**
 * Composer — premium message input with optional image attachment.
 */

import { useEffect, useRef, useState } from 'react';
import type {
  ChangeEvent,
  KeyboardEvent,
  ReactElement,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import styles from './Composer.module.css';

const MAX_TEXTAREA_HEIGHT_REM = 9;

/** Accepted image MIME types. */
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const ACCEPTED_ATTR = ACCEPTED_IMAGE_TYPES.join(',');

/** Max image size: 4 MB (Gemini inline limit is ~4 MB base64). */
const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

export interface AttachedImage {
  mimeType: string;
  base64: string;
  fileName: string;
  /** Object URL for preview — revoked after send. */
  previewUrl: string;
}

export interface ExtraAction {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Highlight the button (e.g. for toggles in the on state). */
  active?: boolean;
}

interface ComposerProps {
  onSend: (text: string, image: AttachedImage | null) => void | Promise<void>;
  disabled?: boolean;
  extraActions?: ExtraAction[];
}

function SendIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        d="M3.4 20.6L21 12 3.4 3.4 3 10.5l12 1.5-12 1.5z"
        fill="currentColor"
      />
    </svg>
  );
}

function ImageIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
        fill="currentColor"
      />
    </svg>
  );
}

function XIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
        fill="currentColor"
      />
    </svg>
  );
}

export function Composer({ onSend, disabled, extraActions }: ComposerProps): ReactElement {
  const { t } = useTranslation();
  const [text, setText] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [attachedImage, setAttachedImage] = useState<AttachedImage | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (el === null) return;
    el.style.height = 'auto';
    const remPx = parseFloat(
      typeof window !== 'undefined'
        ? window.getComputedStyle(document.documentElement).fontSize
        : '16',
    );
    const maxPx = MAX_TEXTAREA_HEIGHT_REM * (Number.isFinite(remPx) ? remPx : 16);
    el.style.height = `${Math.min(el.scrollHeight, maxPx)}px`;
  }, [text]);

  // Revoke object URL on unmount or when image changes.
  useEffect(() => {
    return () => {
      if (attachedImage !== null) {
        URL.revokeObjectURL(attachedImage.previewUrl);
      }
    };
  }, [attachedImage]);

  // Clipboard paste — intercept images pasted anywhere in the composer wrapper.
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent): void => {
      if (disabled === true || isSending) return;
      const items = e.clipboardData?.items;
      if (items === undefined) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item === undefined) continue;
        if (!ACCEPTED_IMAGE_TYPES.includes(item.type)) continue;

        const file = item.getAsFile();
        if (file === null) continue;
        if (file.size > MAX_IMAGE_BYTES) {
          setImageError(t('chat.imageSizeError'));
          return;
        }

        e.preventDefault();
        setImageError(null);

        const reader = new FileReader();
        reader.onload = (ev): void => {
          const result = ev.target?.result;
          if (typeof result !== 'string') return;
          const commaIdx = result.indexOf(',');
          if (commaIdx === -1) return;
          const base64 = result.slice(commaIdx + 1);
          const previewUrl = URL.createObjectURL(file);

          if (attachedImage !== null) {
            URL.revokeObjectURL(attachedImage.previewUrl);
          }

          setAttachedImage({
            mimeType: file.type,
            base64,
            fileName: file.name !== '' ? file.name : `pasted-image.${file.type.split('/')[1] ?? 'png'}`,
            previewUrl,
          });
        };
        reader.readAsDataURL(file);
        return; // only handle the first image item
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [disabled, isSending, attachedImage, t]);

  const trimmed = text.trim();
  const canSend = !disabled && !isSending && (trimmed.length > 0 || attachedImage !== null);

  const handleSend = async (): Promise<void> => {
    if (!canSend) return;
    const payload = trimmed;
    const image = attachedImage;
    setText('');
    setAttachedImage(null);
    setImageError(null);
    setIsSending(true);
    try {
      await onSend(payload, image);
    } finally {
      setIsSending(false);
      // Revoke after send
      if (image !== null) {
        URL.revokeObjectURL(image.previewUrl);
      }
    }
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setText(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  const handleImageButtonClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    // Reset input so the same file can be re-selected after removal.
    event.target.value = '';
    if (file === undefined) return;

    setImageError(null);

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageError(t('chat.imageTypeError'));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setImageError(t('chat.imageSizeError'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e): void => {
      const result = e.target?.result;
      if (typeof result !== 'string') return;
      // result is a data URL: "data:<mime>;base64,<data>"
      const commaIdx = result.indexOf(',');
      if (commaIdx === -1) return;
      const base64 = result.slice(commaIdx + 1);
      const previewUrl = URL.createObjectURL(file);

      // Revoke previous preview if any.
      if (attachedImage !== null) {
        URL.revokeObjectURL(attachedImage.previewUrl);
      }

      setAttachedImage({
        mimeType: file.type,
        base64,
        fileName: file.name,
        previewUrl,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (): void => {
    if (attachedImage !== null) {
      URL.revokeObjectURL(attachedImage.previewUrl);
    }
    setAttachedImage(null);
    setImageError(null);
  };

  const placeholder = t('chat.composerPlaceholder');
  const sendLabel = t('chat.send');
  const attachImageLabel = t('chat.attachImage');

  return (
    <div className={styles.composerWrapper}>
      {/* Image preview strip */}
      <AnimatePresence>
        {attachedImage !== null && (
          <motion.div
            className={styles.imagePreviewStrip}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className={styles.imagePreviewInner}>
              <img
                src={attachedImage.previewUrl}
                alt={attachedImage.fileName}
                className={styles.imagePreviewThumb}
              />
              <span className={styles.imagePreviewName}>{attachedImage.fileName}</span>
              <button
                type="button"
                className={styles.imageRemoveBtn}
                onClick={handleRemoveImage}
                aria-label={t('chat.removeImage')}
                title={t('chat.removeImage')}
              >
                <XIcon />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error message */}
      <AnimatePresence>
        {imageError !== null && (
          <motion.p
            className={styles.imageError}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="alert"
          >
            {imageError}
          </motion.p>
        )}
      </AnimatePresence>

      <div className={styles.composer} data-testid="composer">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_ATTR}
          className={styles.hiddenFileInput}
          onChange={handleFileChange}
          aria-hidden="true"
          tabIndex={-1}
        />

        {/* Image attach button */}
        <motion.button
          type="button"
          className={[
            styles.actionBtn,
            attachedImage !== null ? styles.actionBtnActive : '',
          ]
            .filter((c): c is string => typeof c === 'string' && c.length > 0)
            .join(' ')}
          onClick={handleImageButtonClick}
          disabled={disabled === true || isSending}
          whileHover={!(disabled === true || isSending) ? { scale: 1.1 } : {}}
          whileTap={!(disabled === true || isSending) ? { scale: 0.92 } : {}}
          transition={{ duration: 0.12 }}
          title={attachImageLabel}
          aria-label={attachImageLabel}
        >
          <ImageIcon />
        </motion.button>

        {/* Extra actions (web search, etc.) */}
        {extraActions && extraActions.length > 0 && (
          <div className={styles.extraActions}>
            {extraActions.map((action, idx) => (
              <motion.button
                key={idx}
                type="button"
                className={[styles.actionBtn, action.active === true ? styles.actionBtnActive : '']
                  .filter((c): c is string => typeof c === 'string' && c.length > 0)
                  .join(' ')}
                onClick={action.onClick}
                disabled={action.disabled}
                whileHover={!action.disabled ? { scale: 1.1 } : {}}
                whileTap={!action.disabled ? { scale: 0.92 } : {}}
                transition={{ duration: 0.12 }}
                title={action.label}
                aria-label={action.label}
                aria-pressed={action.active === true ? true : undefined}
              >
                {action.icon}
              </motion.button>
            ))}
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          disabled={disabled === true}
          rows={1}
          className={styles.textarea}
          /*
           * Suppress browser / keyboard autofill suggestions. Some
           * Android keyboards (Samsung, Xiaomi MIUI) show a clipboard
           * suggestion bar above the keyboard with the user's last
           * copied URL — turning autofill off cuts that off when we
           * can, and the unusual `name` value keeps Chrome's
           * password manager from fingerprinting the field as a
           * generic input it should fill.
           */
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="sentences"
          spellCheck={true}
          name="companion-message"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
        />

        <motion.button
          type="button"
          onClick={() => {
            void handleSend();
          }}
          disabled={!canSend}
          whileTap={canSend ? { scale: 0.92 } : {}}
          whileHover={canSend ? { scale: 1.08 } : {}}
          transition={{ duration: 0.12, ease: [0.2, 0.8, 0.2, 1] }}
          className={styles.sendBtn}
          aria-label={sendLabel}
          title={sendLabel}
          data-testid="composer-send"
        >
          <SendIcon />
        </motion.button>
      </div>
    </div>
  );
}
